/* RESPONSIBLE TEAM: team-ai-insights */

import type { CxBenchmarkPercentiles } from 'embercom/components/reporting/bespoke/fin-performance/cx-benchmark-panel/types';

interface PercentileEntry {
  p: number; // probability 0–1 (e.g. 0.25 for p25)
  value: number; // CX score at that percentile
}

// Parses the percentile object (p5, p10, ..., p100) into a sorted array
// of {probability, value} pairs for interpolation.
function parsePercentiles(data: CxBenchmarkPercentiles): PercentileEntry[] {
  return (Object.entries(data) as [keyof CxBenchmarkPercentiles, number][])
    .map(([key, value]) => ({
      p: parseInt(String(key).replace('p', ''), 10) / 100,
      value,
    }))
    .sort((a, b) => a.p - b.p);
}

// Quantile interpolation: given a probability (0–1), returns the CX score.
// This is the inverse of interpolatePercentileRank in cx-benchmark-service.ts —
// that function maps value → percentile, this maps percentile → value.
function interpolateQuantile(p: number, entries: PercentileEntry[]): number {
  if (p <= entries[0].p) {
    return entries[0].value;
  }
  if (p >= entries[entries.length - 1].p) {
    return entries[entries.length - 1].value;
  }

  for (let i = 0; i < entries.length - 1; i++) {
    let lower = entries[i];
    let upper = entries[i + 1];
    if (p >= lower.p && p <= upper.p) {
      let ratio = (p - lower.p) / (upper.p - lower.p);
      return lower.value + ratio * (upper.value - lower.value);
    }
  }

  return entries[Math.floor(entries.length / 2)].value;
}

// Generates synthetic samples by evaluating the quantile function at
// evenly-spaced probability values. This converts the inverse CDF
// (percentile data) into individual data points suitable for KDE.
function generateSamples(entries: PercentileEntry[], n: number): number[] {
  let samples: number[] = [];
  for (let i = 0; i < n; i++) {
    let p = 0.01 + (0.98 * i) / (n - 1);
    samples.push(interpolateQuantile(p, entries));
  }
  return samples;
}

function gaussianKernel(u: number): number {
  return Math.exp(-0.5 * u * u) / Math.sqrt(2 * Math.PI);
}

function mean(arr: number[]): number {
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

function stddev(arr: number[]): number {
  let m = mean(arr);
  let variance = arr.reduce((s, v) => s + (v - m) ** 2, 0) / arr.length;
  return Math.sqrt(variance);
}

function iqr(arr: number[]): number {
  let sorted = [...arr].sort((a, b) => a - b);
  let q1 = sorted[Math.floor(sorted.length * 0.25)];
  let q3 = sorted[Math.floor(sorted.length * 0.75)];
  return q3 - q1;
}

// Silverman's rule of thumb for automatic bandwidth selection.
// Balances smoothness vs detail based on data spread and sample size.
function silvermanBandwidth(samples: number[], multiplier: number): number {
  let sd = stddev(samples);
  let iqrVal = iqr(samples);
  let n = samples.length;
  let spread = Math.min(sd, iqrVal / 1.34);
  return 0.9 * spread * Math.pow(n, -0.2) * multiplier;
}

// Kernel Density Estimation: places a Gaussian bump at each sample point
// and sums them. Returns density values for each evaluation point.
function kde(evalPoints: number[], samples: number[], bandwidth: number): number[] {
  let n = samples.length;
  return evalPoints.map((x) => {
    let density = 0;
    for (let i = 0; i < n; i++) {
      density += gaussianKernel((x - samples[i]) / bandwidth);
    }
    return density / (n * bandwidth);
  });
}

// The main entry point: takes percentile data and returns [x, y] pairs
// suitable for a Highcharts areaspline series.
export function buildDensityCurve(
  percentiles: CxBenchmarkPercentiles,
  options?: { bandwidthMultiplier?: number; numSamples?: number; numEvalPoints?: number },
): [number, number][] {
  let bandwidthMultiplier = options?.bandwidthMultiplier ?? 1.0;
  let numSamples = options?.numSamples ?? 300;
  let numEvalPoints = options?.numEvalPoints ?? 200;

  let entries = parsePercentiles(percentiles);
  let samples = generateSamples(entries, numSamples);
  let h = silvermanBandwidth(samples, bandwidthMultiplier);

  let minX = Math.min(...samples) - 4 * h;
  let maxX = Math.max(...samples) + 4 * h;

  let evalPoints: number[] = [];
  for (let i = 0; i < numEvalPoints; i++) {
    evalPoints.push(minX + ((maxX - minX) * i) / (numEvalPoints - 1));
  }

  let densities = kde(evalPoints, samples, h);

  return evalPoints.map((x, i) => [x, densities[i]]);
}

// Returns the CX score value at a given percentile (0–1).
// Used to position x-axis tick marks at percentile boundaries.
export function getValueAtPercentile(percentiles: CxBenchmarkPercentiles, p: number): number {
  let entries = parsePercentiles(percentiles);
  return interpolateQuantile(p, entries);
}
